// The trade block (TRADES.md, scenario G5): my players marked available
// with what I want back, everyone else's block below, each a tap into the
// builder with that player filled in.
//
// A CLEAN SCREEN (eslint.config.js, CLEAN_SCREENS): drawn only through the kit.

import { useState } from 'react';
import type { TradeBlockEntry } from '@fsp/domain';
import { valueOf } from '@fsp/domain';
import { useAtLeast } from '../../data/detail';
import { useTradeBuilder } from '../../data/hooks';
import { EmptyState, PositionPill, ProTeamCode, StatusBadge, TeamMark } from '../../kit';
import {
  Button,
  Callout,
  Caption,
  Choice,
  Line,
  ListRow,
  RowName,
  RowSub,
  Stack,
  Text,
  TextField,
} from '../../kit/ui';
import { DetailSheet } from '../../templates/DetailSheet';
import { num } from './notComputed';
import { PlayerName, TeamName } from './Names';

export function TradeBlockPanel({
  entries,
  open,
  onOffer,
  onRemove,
  onMark,
}: {
  entries: TradeBlockEntry[];
  /** Trading is open (deadline, playoffs). */
  open: boolean;
  onOffer: (entry: TradeBlockEntry) => void;
  onRemove: (playerId: string) => void;
  onMark: () => void;
}) {
  const mine = entries.filter((e) => e.isMine);
  const theirs = entries.filter((e) => !e.isMine);
  // A projection is a number: from Standard (DETAIL_LEVELS, Simple removes numbers).
  const standard = useAtLeast('standard');
  return (
    <div>
      <div className="fsp-region-head fsp-trade-block-head">
        <Caption>My block</Caption>
        <Button variant="quiet" size="xs" icon="add" onClick={onMark}>
          Mark available
        </Button>
      </div>
      {mine.length === 0 ? (
        <ListRow>
          <Text tone="secondary" block>
            Nothing on your block. Mark a player available and say what you want back.
          </Text>
        </ListRow>
      ) : (
        mine.map((e) => (
          <ListRow
            key={e.player.id}
            lead={<PositionPill pos={e.player.positions[0]!} size="sm" />}
            tall
            trail={
              <Button variant="quiet" size="xs" icon="remove" onClick={() => onRemove(e.player.id)}>
                Remove
              </Button>
            }
          >
            <RowName
              marks={
                <>
                  <ProTeamCode code={e.player.proTeam} />
                  <StatusBadge player={e.player} />
                </>
              }
            >
              <PlayerName player={e.player} />
            </RowName>
            <RowSub wrap>Wants: {e.wants || 'offers'}</RowSub>
          </ListRow>
        ))
      )}
      <div className="fsp-region-head fsp-trade-block-head">
        <Caption>The league's block</Caption>
        <Text tone="secondary" size="xs" numeric>
          {theirs.length}
        </Text>
      </div>
      {theirs.length === 0 ? (
        <EmptyState>Nobody is shopping right now. Yours would be the first.</EmptyState>
      ) : (
        theirs.map((e) => (
          <ListRow
            key={`${e.team.id}:${e.player.id}`}
            lead={<PositionPill pos={e.player.positions[0]!} size="sm" />}
            tall
            onClick={open ? () => onOffer(e) : undefined}
            cover={`Offer for ${e.player.name}`}
            trail={
              open ? (
                <Text tone="accent" weight="strong">
                  Offer
                </Text>
              ) : undefined
            }
          >
            <RowName
              marks={
                <>
                  <ProTeamCode code={e.player.proTeam} />
                  <StatusBadge player={e.player} />
                </>
              }
            >
              <PlayerName player={e.player} />
            </RowName>
            <RowSub wrap>
              <Line gap={1}>
                <TeamMark team={e.team} size={14} />
                <span>
                  <TeamName team={e.team}>{e.team.ownerName}</TeamName> · wants{' '}
                  {e.wants || 'offers'}
                  {standard && e.projection !== null ? (
                    <>
                      {' '}
                      · proj <span className="fsp-num">{num(e.projection)}</span>
                    </>
                  ) : null}
                </span>
              </Line>
            </RowSub>
          </ListRow>
        ))
      )}
    </div>
  );
}

/** Pick one of my players and say what I want back. */
export function MarkAvailableSheet({
  opened,
  onClose,
  onSave,
  blocked,
}: {
  opened: boolean;
  onClose: () => void;
  /** Resolves to the refusal's sentence, or null when he is on the block. */
  onSave: (playerId: string, wants: string) => Promise<string | null>;
  /** Players already on my block. */
  blocked: string[];
}) {
  const builder = useTradeBuilder(
    opened ? { partnerTeamId: null, give: [], get: [], myDrops: [], counterOf: null } : null,
  );
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [wants, setWants] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const mine = builder ? (valueOf(builder)?.mine ?? []) : [];
  const options = mine
    .filter((r) => !blocked.includes(r.player.id))
    .map((r) => ({
      value: r.player.id,
      label: `${r.player.name} · ${r.player.positions[0]}${r.projection !== null ? ` · proj ${num(r.projection)}` : ''}`,
    }));
  return (
    <DetailSheet opened={opened} onClose={onClose} title="Mark a player available">
      <Stack gap={3}>
        <Text tone="secondary" block>
          The league sees him on the block with what you want back. Anyone can start a trade from
          it.
        </Text>
        <Choice
          label="Player"
          placeholder="Pick one of yours"
          options={options}
          value={playerId}
          onChange={setPlayerId}
          empty="Nobody left to shop."
        />
        <TextField
          label="What do you want back?"
          placeholder="A WR2, RB depth, a tight end with a pulse"
          value={wants}
          onChange={setWants}
          maxLength={80}
        />
        {error ? <Callout tone="bad">{error}</Callout> : null}
        <Button
          variant="primary"
          disabled={!playerId || busy}
          onClick={() => {
            if (!playerId) return;
            setBusy(true);
            void onSave(playerId, wants.trim()).then((err) => {
              setBusy(false);
              if (err) {
                setError(err);
                return;
              }
              setPlayerId(null);
              setWants('');
              setError(null);
              onClose();
            });
          }}
        >
          Put him on the block
        </Button>
      </Stack>
    </DetailSheet>
  );
}
